/**
 * Centralized React Query cache configuration
 * Defines stale times based on data volatility and update frequency
 */

import { Duration } from 'luxon';

/**
 * Cache duration constants based on data volatility
 */
export const CacheDuration = {
  /**
   * Very short cache (30 seconds)
   * For highly volatile data that changes frequently
   * Examples: leaderboard, activity history, goal progress
   */
  VOLATILE: Duration.fromObject({ seconds: 30 }).as('milliseconds'),

  /**
   * Short cache (1 minute)
   * For data that changes regularly during active use
   * Examples: group data, participant lists, pledge status
   */
  SHORT: Duration.fromObject({ minutes: 1 }).as('milliseconds'),

  /**
   * Medium cache (5 minutes)
   * For data that changes occasionally
   * Examples: user profiles, communication preferences
   */
  MEDIUM: Duration.fromObject({ minutes: 5 }).as('milliseconds'),

  /**
   * Long cache (15 minutes)
   * For semi-static data that rarely changes
   * Examples: survey definitions, educational content
   */
  LONG: Duration.fromObject({ minutes: 15 }).as('milliseconds'),

  /**
   * Very long cache (1 hour)
   * For static reference data
   * Examples: activity lookup, timeframe lookup
   */
  STATIC: Duration.fromObject({ hours: 1 }).as('milliseconds'),
} as const;

/**
 * Recommended stale times for different view creators
 * Organized by data volatility
 */
export const ViewCreatorStaleTime = {
  // Volatile data - updates frequently during active use
  LeaderboardViewCreator: CacheDuration.VOLATILE,
  ActivityHistoryViewCreator: CacheDuration.VOLATILE,
  GoalCardViewCreator: CacheDuration.VOLATILE,
  PledgePotViewCreator: CacheDuration.VOLATILE,

  // Short cache - updates regularly
  GroupViewCreator: CacheDuration.SHORT,
  ActiveParticipantsViewCreator: CacheDuration.SHORT,
  CohortAccountabilityPartnersViewCreator: CacheDuration.SHORT,
  CohortPledgeViewCreator: CacheDuration.SHORT,
  AdminEngagementReportViewCreator: CacheDuration.SHORT,
  AdminPrizeDrawingsViewCreator: CacheDuration.SHORT,
  NextSurveySectionViewCreator: CacheDuration.SHORT,
  PrizesViewCreator: CacheDuration.SHORT,

  // Medium cache - updates occasionally
  UserProfileViewCreator: CacheDuration.MEDIUM,
  CommunicationPreferencesViewCreator: CacheDuration.MEDIUM,
  DashboardViewCreator: CacheDuration.MEDIUM,

  // Long cache - semi-static data
  GetScienceSomethingViewCreator: CacheDuration.LONG,
  ExerciseInDisguiseContentViewCreator: CacheDuration.LONG,

  // Static cache - reference data
  ActivityLookupViewCreator: CacheDuration.STATIC,
} as const;
